import { apiClient, apiRequestWithOfflineSupport } from './client';
import type { ApiResponse } from '../types/api';
import type { 
  ShopCatalogItem, 
  VirtualItem, 
  CurrencyBalance,
  PurchaseItemRequest,
  Transaction 
} from '../types/shop';

export const shopAPI = {
  // Shop Catalog
  // Backend returns: { data: { catalog: [{ catalogId, itemId, item: {...}, pricePremium, currencyTypeId, isFeatured }], featured: [...], newItems: [...], onSale: [...], total, page, limit, totalPages } }
  // Frontend expects: { data: ShopCatalogItem[] } where ShopCatalogItem has specific field names
  // Transform to extract catalog array and map field names
  getCatalog: () => 
    apiClient.get<any, ApiResponse<any>>('/api/shop/catalog')
      .then(res => ({
        ...res,
        data: (res.data.catalog || []).map((catalogItem: any) => ({
          id: catalogItem.catalogId,
          itemId: catalogItem.itemId,
          price: catalogItem.pricePremium || catalogItem.item?.priceCoins || 0,
          currencyId: catalogItem.currencyTypeId || 1,
          isActive: catalogItem.item?.isActive !== false,
          isFeatured: catalogItem.isFeatured || false,
          discountPercentage: catalogItem.saleDiscount || 0,
          availableFrom: catalogItem.startDate,
          availableUntil: catalogItem.endDate,
          maxPurchases: catalogItem.purchaseLimit,
          createdAt: catalogItem.createdAt,
          updatedAt: catalogItem.item?.updatedAt,
          // Transform nested item data
          item: {
            id: catalogItem.item?.itemId || catalogItem.itemId,
            name: catalogItem.item?.itemName || 'Unknown Item',
            description: catalogItem.item?.description || '',
            category: catalogItem.item?.itemType || 'general',
            rarity: 'common', // Default rarity since not in API
            metadata: {
              icon: catalogItem.item?.iconUrl || '🎴',
              image: catalogItem.item?.iconUrl,
              tags: catalogItem.item?.tags || [],
              consumable: catalogItem.item?.itemType === 'consumable',
              stackable: catalogItem.item?.isStackable || false,
              maxStack: catalogItem.item?.maxStack || 1
            },
            createdAt: catalogItem.item?.createdAt,
            updatedAt: catalogItem.item?.updatedAt
          },
          // Create currency object (will be populated by separate call)
          currency: {
            id: catalogItem.currencyTypeId || 1,
            name: 'Coins',
            symbol: 'COIN',
            description: 'Game currency',
            isSoft: true,
            metadata: {
              icon: '🪙',
              color: '#FFD700'
            }
          }
        }))
      })),
  
  // Backend returns: { data: { items: [...], total, page, limit, totalPages } }
  // Frontend expects: { data: VirtualItem[] }
  // Transform to extract items array
  getItems: () => 
    apiClient.get<any, ApiResponse<any>>('/api/shop/items')
      .then(res => ({
        ...res,
        data: res.data.items || []  // Extract items array, fallback to empty array
      })),
  
  // Backend returns: { data: { item: {...} } }
  // Frontend expects: { data: VirtualItem }
  // Transform to extract item object
  getItem: (id: number) => 
    apiClient.get<any, ApiResponse<any>>(`/api/shop/items/${id}`)
      .then(res => ({
        ...res,
        data: res.data.item || null  // Extract item object
      })),
  
  // Backend returns: { data: { featured: [...], total } }
  // Frontend expects: { data: ShopCatalogItem[] }
  // Transform to extract featured array and map field names
  getFeaturedItems: () => 
    apiClient.get<any, ApiResponse<any>>('/api/shop/featured')
      .then(res => ({
        ...res,
        data: (res.data.featured || []).map((catalogItem: any) => ({
          id: catalogItem.catalogId,
          itemId: catalogItem.itemId,
          price: catalogItem.pricePremium || catalogItem.item?.priceCoins || 0,
          currencyId: catalogItem.currencyTypeId || 1,
          isActive: catalogItem.item?.isActive !== false,
          isFeatured: catalogItem.isFeatured || false,
          discountPercentage: catalogItem.saleDiscount || 0,
          availableFrom: catalogItem.startDate,
          availableUntil: catalogItem.endDate,
          maxPurchases: catalogItem.purchaseLimit,
          createdAt: catalogItem.createdAt,
          updatedAt: catalogItem.item?.updatedAt,
          // Transform nested item data
          item: {
            id: catalogItem.item?.itemId || catalogItem.itemId,
            name: catalogItem.item?.itemName || 'Unknown Item',
            description: catalogItem.item?.description || '',
            category: catalogItem.item?.itemType || 'general',
            rarity: 'common', // Default rarity since not in API
            metadata: {
              icon: catalogItem.item?.iconUrl || '🎴',
              image: catalogItem.item?.iconUrl,
              tags: catalogItem.item?.tags || [],
              consumable: catalogItem.item?.itemType === 'consumable',
              stackable: catalogItem.item?.isStackable || false,
              maxStack: catalogItem.item?.maxStack || 1
            },
            createdAt: catalogItem.item?.createdAt,
            updatedAt: catalogItem.item?.updatedAt
          },
          // Create currency object
          currency: {
            id: catalogItem.currencyTypeId || 1,
            name: 'Coins',
            symbol: 'COIN',
            description: 'Game currency',
            isSoft: true,
            metadata: {
              icon: '🪙',
              color: '#FFD700'
            }
          }
        }))
      })),
  
  // Backend returns: { data: { categories: [{ category, itemCount, items }], totalCategories, totalItems } }
  // Frontend expects: { data: { category: string; count: number; items: any[] }[] }
  // Transform to extract categories array WITH items
  getCategories: () => 
    apiClient.get<any, ApiResponse<any>>('/api/shop/categories')
      .then(res => ({
        ...res,
        data: (res.data.categories || []).map((cat: any) => ({
          category: cat.category,
          count: cat.itemCount || cat.items?.length || 0,
          items: cat.items || []  // ← KEEP THE ITEMS ARRAY!
        }))
      })),
  
  // Currency
  // Backend returns: { data: { balances: [{ id, currencyType: { id, code, name }, amount }] } }
  // Frontend expects: { data: CurrencyBalance[] } where CurrencyBalance = { currencyId, balance, currency: { metadata } }
  // Transform to match expected structure
  getCurrencyBalances: () => 
    apiClient.get<any, ApiResponse<any>>('/api/currency/balances')
      .then(res => ({
        ...res,
        data: (res.data.balances || []).map((balance: any) => ({
          currencyId: balance.currencyType?.id || balance.id,
          balance: balance.amount || 0,
          currency: {
            id: balance.currencyType?.id || balance.id,
            name: balance.currencyType?.name || 'Unknown',
            symbol: balance.currencyType?.code || 'COIN',
            description: balance.currencyType?.name || 'Currency',
            isSoft: !balance.currencyType?.isPremium,
            metadata: {
              icon: balance.currencyType?.iconUrl || '🪙',
              color: '#FFD700'
            }
          }
        }))
      })),
  
  // Purchases (with offline support)
  purchaseItem: (data: PurchaseItemRequest) => 
    apiRequestWithOfflineSupport<ApiResponse<Transaction>>(
      '/api/purchase/item',
      'POST',
      data,
      { queueable: true, actionType: 'PURCHASE_ITEM' }
    ),
  
  // Transactions
  getTransactions: (params?: { page?: number; limit?: number; type?: string }) => 
    apiClient.get<any, ApiResponse<{ transactions: Transaction[]; total: number; page: number; limit: number }>>('/api/transactions', { params }),
  
  getTransaction: (id: string) => 
    apiClient.get<any, ApiResponse<Transaction>>(`/api/transactions/${id}`),
};